/**
 * manager-product.js
 * Gerencia troca de cor, carrossel interno e compra rapida dos product cards.
 */

class ProductManager {
  constructor() {
    this.selector = '[data-product-card]';
    this._onClick = this._onClick.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);

    document.addEventListener('click', this._onClick);
    document.addEventListener('shopify:section:load', this._handleSectionLoad);

    this.init();
  }

  init(scope = document) {
    const cards = scope.matches?.(this.selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.selector) || []);

    cards.forEach((card) => this._registerCard(card));
  }

  _handleSectionLoad(event) {
    this.init(event.target);
  }

  _registerCard(card) {
    if (!card || card.dataset.productCardReady === 'true') return;

    // Ordenar TODAS as cores, inclusive as ocultas no painel "+", antes de
    // redistribuir os lugares visiveis. A ordenacao por container isolado
    // deixava cores esgotadas na frente das disponiveis no painel extra.
    const visible = card.querySelector('.productCard__swatches');
    const panel = card.querySelector('[data-product-card-swatch-panel]');
    if (visible) {
      const visibleItems = Array.from(visible.children).filter(el => el.matches?.('[data-product-card-swatch]'));
      const panelItems = panel ? Array.from(panel.children).filter(el => el.matches?.('[data-product-card-swatch]')) : [];
      const limit = visibleItems.length;
      const all = [...visibleItems, ...panelItems];
      const ordered = [
        ...all.filter(el => el.dataset.available !== 'false'),
        ...all.filter(el => el.dataset.available === 'false')
      ];
      ordered.slice(0, limit).forEach(el => visible.appendChild(el));
      if (panel) ordered.slice(limit).forEach(el => panel.appendChild(el));
      const more = visible.querySelector('[data-product-card-more-button]');
      if (more) visible.appendChild(more);
    }
    this._bindSwatchPanelHover(card);
    const activeSwatch = card.querySelector('[data-product-card-swatch].is-active')
      || card.querySelector('[data-product-card-swatch]');
    const activeSwatchUrl = activeSwatch
      ? this._getVariantUrl(activeSwatch.dataset.url || '', activeSwatch.dataset.defaultVariantId || '')
      : '';

    this._syncStockNotify(card, activeSwatch);

    const titleLink = card.querySelector('[data-product-card-title-link]');
    if (titleLink && activeSwatchUrl) titleLink.setAttribute('href', activeSwatchUrl);

    const mediaLink = card.querySelector('[data-product-card-media-link]');
    if (!this._isCarouselMode(card) && mediaLink && activeSwatchUrl) {
      mediaLink.setAttribute('href', activeSwatchUrl);
    }

    const sizePopup = card.querySelector('[data-product-card-size-popup]');
    const sizeScroller = sizePopup?.querySelector('.productCard__sizeDropdownScroller');
    sizeScroller?.addEventListener('scroll', () => this._syncSizeScrollState(sizePopup), { passive: true });

    if (this._isCarouselMode(card)) {
      this._setupCarousel(card);
      this._syncCarousel(
        card,
        card.dataset.selectedGalleryKey || card.dataset.selectedHandle || '',
        activeSwatchUrl
      );
    }

    if (activeSwatch) {
      card.dataset.selectedVariantPrice = activeSwatch.dataset.defaultVariantPrice || card.dataset.selectedVariantPrice || '';
      this._setExtraState(card, activeSwatch);
      this._syncPricingVisibility(card, activeSwatch);
      this._syncPromotionStrip(card, activeSwatch);
      this._syncAddButton(card, activeSwatch.dataset.available === 'true');
      this._syncSizeLists(card, activeSwatch.dataset.handle || card.dataset.selectedHandle || '', activeSwatch.dataset.colorHandle || '');
    } else {
      this._syncAddButton(card);
    }

    card.dataset.productCardReady = 'true';
  }

  _onClick(event) {
    const moreButton = event.target.closest('[data-product-card-more-button]');
    if (moreButton) {
      const card = moreButton.closest('[data-product-card]');
      if (!card) return;

      event.preventDefault();
      event.stopPropagation();

      const isOpen = moreButton.getAttribute('aria-expanded') === 'true';
      moreButton.classList.remove('is-hover-preview');
      this._toggleSwatchPanel(card, !isOpen);
      return;
    }

    const carouselPrevBtn = event.target.closest('[data-product-card-carousel-prev]');
    if (carouselPrevBtn) {
      event.preventDefault();
      const card = carouselPrevBtn.closest('[data-product-card]');
      if (card) this._stepCarousel(card, -1);
      return;
    }

    const carouselNextBtn = event.target.closest('[data-product-card-carousel-next]');
    if (carouselNextBtn) {
      event.preventDefault();
      const card = carouselNextBtn.closest('[data-product-card]');
      if (card) this._stepCarousel(card, 1);
      return;
    }

    const swatchBtn = event.target.closest('[data-product-card-swatch]');
    if (swatchBtn) {
      const card = swatchBtn.closest('[data-product-card]');
      if (card) {
        event.preventDefault();
        this._selectColor(card, swatchBtn);
      }
      return;
    }

    const addBtn = event.target.closest('[data-product-card-add]');
    if (addBtn) {
      const card = addBtn.closest('[data-product-card]');
      if (card) {
        event.preventDefault();
        this._handleAddToCart(card, addBtn);
      }
      return;
    }

    const addToggle = event.target.closest('[data-product-card-add-toggle]');
    if (addToggle) {
      const card = addToggle.closest('[data-product-card]');
      if (!card || addToggle.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      this._toggleSizePopup(card, addToggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const sizeScrollControl = event.target.closest('[data-product-card-size-scroll]');
    if (sizeScrollControl) {
      const popup = sizeScrollControl.closest('[data-product-card-size-popup]');
      const scroller = popup?.querySelector('.productCard__sizeDropdownScroller');
      if (!scroller) return;
      event.preventDefault();
      event.stopPropagation();
      scroller.scrollBy({
        left: (Number(sizeScrollControl.dataset.productCardSizeScroll) || 1) * 90,
        behavior: 'smooth'
      });
      return;
    }

    const sizePopupClose = event.target.closest('[data-product-card-size-popup-close]');
    if (sizePopupClose) {
      const card = sizePopupClose.closest('[data-product-card]');
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();
      this._toggleSizePopup(card, false);
      card.querySelector('[data-product-card-add-toggle]')?.focus();
      return;
    }

    const sizeOption = event.target.closest('[data-product-card-size-popup] [data-size-option]');
    if (sizeOption) {
      const card = sizeOption.closest('[data-product-card]');
      if (!card || sizeOption.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      card.dataset.selectedVariantId = sizeOption.dataset.variantId || '';
      card.dataset.selectedVariantPrice = sizeOption.dataset.price || '';
      this._toggleSizePopup(card, false);
      this._addToCart(sizeOption, card.dataset.selectedVariantId);
      return;
    }

    if (!event.target.closest('[data-product-card-swatch-panel], [data-product-card-size-popup]')) {
      this._closeAllPanels();
    }
  }

  _isCarouselMode(card) {
    return card.dataset.mediaMode === 'color_carousel';
  }

  _toggleSwatchPanel(card, forceState) {
    const panel = card.querySelector('[data-product-card-swatch-panel]');
    const button = card.querySelector('[data-product-card-more-button]');
    if (!panel || !button) return;

    const isOpen = typeof forceState === 'boolean'
      ? forceState
      : panel.hidden;

    this._closeAllPanels(card);

    if (isOpen) {
      panel.hidden = false;
      panel.style.visibility = 'hidden';
      panel.style.left = `${button.offsetLeft}px`;
      panel.style.top = '0px';

      const top = -22;

      panel.style.left = `${button.offsetLeft}px`;
      panel.style.top = `${top}px`;
      panel.style.visibility = '';
    } else {
      panel.style.visibility = '';
      panel.hidden = true;
    }

    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    button.classList.toggle('is-open', isOpen);
  }

  _syncSizeScrollState(popup) {
    const scroller = popup?.querySelector('.productCard__sizeDropdownScroller');
    if (!scroller) return;
    const limit = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    popup.classList.toggle('is-size-scrollable', limit > 2);
    popup.classList.toggle('at-size-start', scroller.scrollLeft <= 2);
    popup.classList.toggle('at-size-end', scroller.scrollLeft >= limit - 2);
  }

  _toggleSizePopup(card, forceState) {
    const popup = card.querySelector('[data-product-card-size-popup]');
    const button = card.querySelector('[data-product-card-add-toggle]');
    if (!popup || !button) return;

    const isOpen = typeof forceState === 'boolean' ? forceState : popup.hidden;
    this._closeAllPanels(card);
    popup.hidden = !isOpen;
    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (isOpen) {
      const scroller = popup.querySelector('.productCard__sizeDropdownScroller');
      if (scroller) scroller.scrollLeft = 0;
      window.requestAnimationFrame(() => this._syncSizeScrollState(popup));
    }
  }

  _syncSizeLists(card, handle, colorHandle = '') {
    card.querySelectorAll('[data-product-card-size-popup] [data-product-handle]').forEach((list) => {
      list.hidden = list.dataset.productHandle !== handle;

      if (list.hidden || !colorHandle) return;

      const options = Array.from(list.querySelectorAll('[data-size-option][data-color-handle]'));
      if (!options.some((option) => option.dataset.colorHandle)) return;

      options.forEach((option) => {
        option.closest('li').hidden = option.dataset.colorHandle !== colorHandle;
      });
    });
  }

  _bindSwatchPanelHover(card) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const cluster = card.querySelector('.productCard__swatchCluster');
    const button = card.querySelector('[data-product-card-more-button]');
    const panel = card.querySelector('[data-product-card-swatch-panel]');
    if (!cluster || !button || !panel || cluster.dataset.hoverBound === 'true') return;

    cluster.dataset.hoverBound = 'true';

    cluster.addEventListener('mouseenter', () => {
      if (button.getAttribute('aria-expanded') === 'true') return;
      button.classList.add('is-hover-preview');
      this._toggleSwatchPanel(card, true);
    });

    cluster.addEventListener('mouseleave', () => {
      if (!button.classList.contains('is-hover-preview')) return;
      button.classList.remove('is-hover-preview');
      this._toggleSwatchPanel(card, false);
    });
  }

  _closeAllPanels(exceptCard = null) {
    document.querySelectorAll('[data-product-card-swatch-panel]').forEach((panel) => {
      const card = panel.closest('[data-product-card]');
      if (exceptCard && card === exceptCard) return;

      panel.hidden = true;
      panel.style.visibility = '';

      const button = card?.querySelector('[data-product-card-more-button]');
      button?.setAttribute('aria-expanded', 'false');
      button?.classList.remove('is-open');
      button?.classList.remove('is-hover-preview');
    });

    document.querySelectorAll('[data-product-card-size-popup]').forEach((popup) => {
      const card = popup.closest('[data-product-card]');
      if (exceptCard && card === exceptCard) return;

      popup.hidden = true;
      card?.querySelector('[data-product-card-add-toggle]')?.setAttribute('aria-expanded', 'false');
    });
  }

  _setExtraState(card, swatchBtn) {
    const panel = card.querySelector('[data-product-card-swatch-panel]');
    const moreButton = card.querySelector('[data-product-card-more-button]');
    if (!moreButton) return;

    const isExtra = Boolean(panel && panel.contains(swatchBtn));
    moreButton.classList.toggle('is-active', isExtra);
    card.dataset.extraActive = isExtra ? 'true' : 'false';
  }

  _getVariantUrl(url, variantId) {
    if (!url) return '';

    try {
      const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:/i.test(url);
      const targetUrl = new URL(url, window.location.href);
      if (variantId) targetUrl.searchParams.set('variant', variantId);

      if (isAbsoluteUrl) return targetUrl.href;
      return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    } catch (error) {
      return url;
    }
  }

  _selectColor(card, swatchBtn) {
    const handle = swatchBtn.dataset.handle;
    if (!handle) return;
    const colorHandle = swatchBtn.dataset.colorHandle || '';
    const galleryKey = swatchBtn.dataset.galleryKey || handle;
    const variantUrl = this._getVariantUrl(swatchBtn.dataset.url || '', swatchBtn.dataset.defaultVariantId || '');

    card.dataset.selectedHandle = handle;
    card.dataset.selectedGalleryKey = galleryKey;
    card.dataset.selectedVariantId = swatchBtn.dataset.defaultVariantId || '';
    card.dataset.selectedVariantPrice = swatchBtn.dataset.defaultVariantPrice || '';

    card.querySelectorAll('[data-product-card-swatch]').forEach((button) => {
      button.classList.toggle('is-active', button === swatchBtn);
    });

    const titleLink = card.querySelector('[data-product-card-title-link]');
    if (titleLink && variantUrl) {
      titleLink.setAttribute('href', variantUrl);
    }
    if (variantUrl) {
      card.querySelectorAll('[data-mini-pdp-trigger]').forEach((trigger) => {
        trigger.dataset.miniPdpTrigger = variantUrl;
      });
    }

    const mediaLink = card.querySelector('[data-product-card-media-link]');
    if (!this._isCarouselMode(card) && mediaLink && variantUrl) {
      mediaLink.setAttribute('href', variantUrl);
    }

    const title = card.querySelector('[data-product-card-title]');
    if (title && swatchBtn.dataset.title) {
      title.textContent = swatchBtn.dataset.title;
    }

    const price = card.querySelector('[data-product-card-price]');
    if (price && swatchBtn.dataset.price) {
      price.textContent = swatchBtn.dataset.price;
    }
    const compareAt = card.querySelector('[data-product-card-compare-at-price]');
    const compareAtText = swatchBtn.dataset.compareAtPrice || '';
    if (compareAt) {
      compareAt.textContent = compareAtText;
      compareAt.hidden = !compareAtText;
    }
    const discountBadge = card.querySelector('[data-product-card-discount-badge]');
    const discountPercent = Number(swatchBtn.dataset.discountPercent || 0);
    if (discountBadge) {
      discountBadge.hidden = discountPercent <= 0;
      discountBadge.textContent = discountPercent > 0 ? `-${discountPercent}%` : '';
    }
    const newInBadge = card.querySelector('[data-product-card-new-in-badge]');
    if (newInBadge) {
      newInBadge.hidden = swatchBtn.dataset.newIn !== 'true';
    }

    const installmentsWrap = card.querySelector('[data-product-card-installments-wrap]');
    const installments = card.querySelector('[data-product-card-installments]');
    const hasInstallments = Boolean(swatchBtn.dataset.installments);
    if (installmentsWrap) {
      installmentsWrap.hidden = !hasInstallments;
    }
    if (installments && hasInstallments) {
      let installmentText = swatchBtn.dataset.installments;
      // Mantem o formato compacto do minicart inclusive apos trocar a cor.
      if (card.closest('[data-drawer="cart"]')) {
        installmentText = installmentText.replace(/^(\d+x)\s+de\s+(?:R\$\s*)?/i, '$1 ');
      }
      installments.textContent = installmentText;
    }

    if (!this._isCarouselMode(card)) {
      const snapshot = this._createColorSwapSnapshot(card);
      const primaryImage = card.querySelector('[data-product-card-image-primary]');
      if (primaryImage) primaryImage.style.visibility = swatchBtn.dataset.image ? '' : 'hidden';
      if (primaryImage && swatchBtn.dataset.image) {
        primaryImage.setAttribute('src', swatchBtn.dataset.image);
        primaryImage.setAttribute('alt', swatchBtn.dataset.alt || swatchBtn.dataset.title || '');
        primaryImage.removeAttribute('srcset');
        primaryImage.removeAttribute('sizes');
      }

      const secondaryImage = card.querySelector('[data-product-card-image-secondary]');
      if (secondaryImage) {
        secondaryImage.style.visibility = swatchBtn.dataset.image ? '' : 'hidden';
        const secondarySrc = swatchBtn.dataset.secondaryImage || swatchBtn.dataset.image || '';
        const hasSecondaryImage = Boolean(swatchBtn.dataset.secondaryImage);
        card.dataset.hasSecondaryImage = hasSecondaryImage ? 'true' : 'false';
        card.classList.toggle('productCard--hasSecondaryImage', hasSecondaryImage);

        if (secondarySrc) {
          secondaryImage.setAttribute('src', secondarySrc);
          secondaryImage.setAttribute('alt', swatchBtn.dataset.alt || swatchBtn.dataset.title || '');
          secondaryImage.removeAttribute('srcset');
          secondaryImage.removeAttribute('sizes');
        }
      }

      this._finishColorCrossfade(card, primaryImage, snapshot);
    } else {
      const snapshot = this._createColorSwapSnapshot(card);
      this._syncCarousel(card, galleryKey, variantUrl);
      const activeImage = card.querySelector('[data-product-card-carousel-slide].is-active img');
      this._finishColorCrossfade(card, activeImage, snapshot);
    }

    this._setExtraState(card, swatchBtn);
    this._syncPricingVisibility(card, swatchBtn);
    this._syncPromotionStrip(card, swatchBtn);
    this._toggleSwatchPanel(card, false);
    this._toggleSizePopup(card, false);
    this._syncSizeLists(card, handle, colorHandle);
    this._syncAddButton(card, swatchBtn.dataset.available === 'true');
    this._syncStockNotify(card, swatchBtn);
  }

  _createColorSwapSnapshot(card) {
    if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    const media = card.querySelector('.productCard__media');
    const sourceImage = card.querySelector('[data-product-card-carousel-slide].is-active img')
      || card.querySelector('[data-product-card-image-primary]');

    if (!media || !sourceImage || !sourceImage.currentSrc && !sourceImage.src) return null;

    media.querySelectorAll('[data-color-swap-snapshot]').forEach((node) => node.remove());

    const snapshot = sourceImage.cloneNode(false);
    snapshot.removeAttribute('id');
    snapshot.removeAttribute('srcset');
    snapshot.removeAttribute('sizes');
    snapshot.removeAttribute('loading');
    snapshot.setAttribute('aria-hidden', 'true');
    snapshot.setAttribute('data-color-swap-snapshot', '');
    snapshot.className = 'productCard__colorSwapSnapshot';
    snapshot.src = sourceImage.currentSrc || sourceImage.src;

    media.appendChild(snapshot);
    return snapshot;
  }

  _finishColorCrossfade(card, incomingImage, snapshot) {
    if (!card || !snapshot || !incomingImage) {
      snapshot?.remove();
      return;
    }

    incomingImage.classList.add('is-color-crossfade-enter');

    let finished = false;
    const reveal = () => {
      if (finished) return;
      finished = true;

      requestAnimationFrame(() => {
        incomingImage.classList.add('is-color-crossfade-visible');

        window.setTimeout(() => {
          snapshot.remove();
          incomingImage.classList.remove('is-color-crossfade-enter', 'is-color-crossfade-visible');
        }, 470);
      });
    };

    if (incomingImage.complete && incomingImage.naturalWidth > 0) {
      reveal();
    } else {
      incomingImage.addEventListener('load', reveal, { once: true });
      incomingImage.addEventListener('error', reveal, { once: true });
      window.setTimeout(reveal, 900);
    }
  }

  _syncPricingVisibility(card, swatchBtn) {
    const unavailable = swatchBtn?.dataset.available === 'false';
    const prices = card.querySelector('.productCard__prices');
    const installmentsWrap = card.querySelector('[data-product-card-installments-wrap]');

    if (prices) prices.hidden = unavailable;

    if (installmentsWrap && unavailable) {
      installmentsWrap.hidden = true;
    }
  }

  _syncPromotionStrip(card, swatchBtn) {
    const strip = card.querySelector('[data-iorane-promo-strip]');
    if (!strip) return;

    const discountPercent = Number(swatchBtn?.dataset.discountPercent || 0);
    strip.hidden = discountPercent <= 0;
  }

  _syncStockNotify(card, swatchBtn) {
    const link = card.querySelector('[data-product-card-notify-button]');
    const toggle = card.querySelector('[data-product-card-add-toggle]');
    const unavailable = swatchBtn?.dataset.available === 'false';
    const url = swatchBtn ? this._getVariantUrl(swatchBtn.dataset.url || '', swatchBtn.dataset.defaultVariantId || '') : '';
    if (link) {
      link.hidden = !unavailable || !url;
      if (url) {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('avise_me', '1');
        link.href = target.pathname + target.search + target.hash;
      }
    }
    if (toggle) toggle.hidden = unavailable;
  }

  _syncAddButton(card, availableOverride = null) {
    const addBtn = card.querySelector('[data-product-card-add]');
    const addToggle = card.querySelector('[data-product-card-add-toggle]');
    if (!addBtn && !addToggle) return;

    const variantId = card.dataset.selectedVariantId || '';
    const hasPositivePrice = Number(card.dataset.selectedVariantPrice) > 0;
    const canAdd = availableOverride === null
      ? Boolean(variantId) && hasPositivePrice
      : Boolean(variantId) && availableOverride && hasPositivePrice;

    if (addBtn) addBtn.disabled = !canAdd;
    if (addToggle) addToggle.disabled = !canAdd;
  }

  async _handleAddToCart(card, btn) {
    const variantId = card.dataset.selectedVariantId || '';
    if (!variantId || btn.disabled || Number(card.dataset.selectedVariantPrice) <= 0) return;
    await this._addToCart(btn, variantId);
  }

  async _addToCart(btn, variantId) {
    const card = btn.closest('[data-product-card]');
    const variantPrice = btn.dataset.price || card?.dataset.selectedVariantPrice;
    if (variantPrice === undefined || Number(variantPrice) <= 0) return;
    btn.style.width = `${btn.offsetWidth}px`;
    btn.style.height = `${btn.offsetHeight}px`;

    await window.CartManager?.add(variantId, {
      button: btn,
      action: 'open_drawer',
      variantPrice,
    });

    btn.style.width = '';
    btn.style.height = '';
  }

  _setupCarousel(card) {
    if (card.dataset.carouselReady === 'true') return;

    const carousel = card.querySelector('[data-product-card-carousel]');
    const track = card.querySelector('[data-product-card-carousel-track]');
    if (!carousel || !track) return;

    if (card.dataset.carouselDrag === 'true') {
      this._bindCarouselDrag(card, carousel);
    }

    card.dataset.carouselReady = 'true';
  }

  _syncCarousel(card, handle, productUrl = '') {
    const template = Array.from(card.querySelectorAll('template[data-product-card-gallery-template]'))
      .find((node) => node.dataset.productCardGalleryTemplate === handle);
    const track = card.querySelector('[data-product-card-carousel-track]');
    const controls = card.querySelector('[data-product-card-carousel-controls]');
    if (!template || !track || !controls) return;

    track.innerHTML = template.innerHTML;

    if (productUrl) {
      track.querySelectorAll('[data-product-card-carousel-slide]').forEach((slide) => {
        slide.setAttribute('href', productUrl);
      });
    }

    const slides = Array.from(track.querySelectorAll('[data-product-card-carousel-slide]'));
    slides.forEach((slide, index) => {
      const isActive = index === 0;
      slide.classList.remove('is-leaving', 'is-entering', 'from-next', 'from-prev', 'to-next', 'to-prev');
      slide.classList.toggle('is-active', isActive);
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const showArrows = card.dataset.carouselArrows === 'true';
    const hideControls = slides.length <= 1 || !showArrows;
    controls.hidden = hideControls;
    controls.style.display = hideControls ? 'none' : '';
  }

  _bindCarouselDrag(card, carousel) {
    // Desktop-only image dragging. On mobile, let the browser own vertical scroll.
    if (window.matchMedia('(max-width: 749px)').matches) return;
    const dragState = {
      active: false,
      moved: false,
      startX: 0,
      startY: 0,
      pointerId: null,
      suppressClick: false,
    };
    const threshold = 18;
    const hasPointerEvents = 'PointerEvent' in window;

    const resetDragState = () => {
      dragState.active = false;
      dragState.moved = false;
      dragState.startX = 0;
      dragState.startY = 0;
      dragState.pointerId = null;
      carousel.classList.remove('is-dragging');
    };

    const beginDrag = (clientX, clientY, pointerId = null) => {
      dragState.active = true;
      dragState.moved = false;
      dragState.startX = clientX;
      dragState.startY = clientY;
      dragState.pointerId = pointerId;
      dragState.suppressClick = false;
      carousel.classList.add('is-dragging');
    };

    const updateDrag = (clientX, clientY) => {
      if (!dragState.active) return false;

      const deltaX = clientX - dragState.startX;
      const deltaY = clientY - dragState.startY;

      if (Math.abs(deltaX) <= threshold || Math.abs(deltaX) <= Math.abs(deltaY)) return false;

      dragState.moved = true;
      dragState.suppressClick = true;
      return true;
    };

    const finishDrag = (clientX, clientY) => {
      if (!dragState.active) return;

      const deltaX = clientX - dragState.startX;
      const deltaY = clientY - dragState.startY;
      const shouldStep = Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY);

      if (dragState.moved && shouldStep) {
        this._stepCarousel(card, deltaX < 0 ? 1 : -1);
      }

      window.setTimeout(() => {
        dragState.suppressClick = false;
      }, 0);

      resetDragState();
    };

    carousel.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    carousel.querySelectorAll('a, img').forEach((node) => {
      node.setAttribute('draggable', 'false');
    });

    if (hasPointerEvents) {
      carousel.addEventListener('pointerdown', (event) => {
        if (window.matchMedia('(max-width: 749px)').matches) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('[data-product-card-carousel-prev], [data-product-card-carousel-next]')) return;

        beginDrag(event.clientX, event.clientY, event.pointerId);

        if (carousel.setPointerCapture) {
          carousel.setPointerCapture(event.pointerId);
        }
      });

      carousel.addEventListener('pointermove', (event) => {
        if (!dragState.active || dragState.pointerId !== event.pointerId) return;

        if (updateDrag(event.clientX, event.clientY)) {
          event.preventDefault();
        }
      });

      const handlePointerEnd = (event) => {
        if (!dragState.active || dragState.pointerId !== event.pointerId) return;

        if (carousel.releasePointerCapture && carousel.hasPointerCapture?.(event.pointerId)) {
          carousel.releasePointerCapture(event.pointerId);
        }

        finishDrag(event.clientX, event.clientY);
      };

      carousel.addEventListener('pointerup', handlePointerEnd);
      carousel.addEventListener('pointercancel', handlePointerEnd);
    } else {
      carousel.addEventListener('mousedown', (event) => {
        if (window.matchMedia('(max-width: 749px)').matches) return;
        if (event.button !== 0) return;
        if (event.target.closest('[data-product-card-carousel-prev], [data-product-card-carousel-next]')) return;
        beginDrag(event.clientX, event.clientY);
      });

      window.addEventListener('mousemove', (event) => {
        if (!dragState.active) return;
        if (updateDrag(event.clientX, event.clientY)) {
          event.preventDefault();
        }
      });

      window.addEventListener('mouseup', (event) => {
        if (!dragState.active) return;
        finishDrag(event.clientX, event.clientY);
      });
    }

    carousel.addEventListener('click', (event) => {
      if (dragState.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const activeLink = event.target.closest('a[href]') || carousel.querySelector('[data-product-card-carousel-slide].is-active[href]');
      if (!activeLink) return;

      if (card.hasAttribute('data-mini-pdp-card') && window.CartDrawerManager?.openMiniPdp) {
        event.preventDefault();
        event.stopPropagation();
        window.CartDrawerManager.openMiniPdp(activeLink.href, activeLink);
        return;
      }

      event.preventDefault();
      window.location.assign(activeLink.href);
    }, true);
  }

  _stepCarousel(card, direction) {
    const slides = Array.from(card.querySelectorAll('[data-product-card-carousel-track] [data-product-card-carousel-slide]'));
    if (slides.length < 2) return;

    const currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (safeIndex + direction + slides.length) % slides.length;
    if (nextIndex === safeIndex) return;

    const currentSlide = slides[safeIndex];
    const nextSlide = slides[nextIndex];
    const enterClass = direction > 0 ? 'from-next' : 'from-prev';
    const leaveClass = direction > 0 ? 'to-prev' : 'to-next';

    slides.forEach((slide) => {
      slide.classList.remove('is-entering', 'is-leaving', 'from-next', 'from-prev', 'to-next', 'to-prev');
    });

    nextSlide.hidden = false;
    nextSlide.setAttribute('aria-hidden', 'false');
    nextSlide.classList.add('is-entering', enterClass);

    currentSlide.hidden = false;
    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.classList.add('is-leaving', leaveClass);

    requestAnimationFrame(() => {
      nextSlide.classList.add('is-active');
      nextSlide.classList.remove('from-next', 'from-prev');
      currentSlide.classList.remove('is-active');
      currentSlide.classList.remove('to-next', 'to-prev');
    });

    window.setTimeout(() => {
      currentSlide.hidden = true;
      currentSlide.classList.remove('is-leaving');
      nextSlide.classList.remove('is-entering');
    }, 520);
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('shopify:section:load', this._handleSectionLoad);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ProductManager = new ProductManager();
  });
} else {
  window.ProductManager = new ProductManager();
}



